import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Clock, FileText, Download, ShieldCheck, Upload, AlertTriangle, Loader2, RefreshCw,
} from "lucide-react";

export default function PaymentPending() {
  const [state, setState] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [docType, setDocType] = useState("id_proof");
  const nav = useNavigate();

  const load = async () => {
    const [s, i, d] = await Promise.all([
      api.get("/onboarding/state"),
      api.get("/invoices"),
      api.get("/kyc/documents"),
    ]);
    setState(s.data); setInvoices(i.data); setDocs(d.data);
    if (s.data.next_step === "sign_contract") nav("/onboarding/contract");
    if (s.data.next_step === "dashboard") nav("/dashboard");
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);  // poll every 30s
    return () => clearInterval(id);
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded. Admin will review shortly.");
      await load();
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!state) return <div className="min-h-screen bg-[#05050A] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  const sub = state.subscription;
  const isBlocked = ["declined", "fraud", "risk", "suspended"].includes(sub?.status);
  const needsDocs = sub?.status === "review_required";

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg p-6 lg:p-12">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" /> Payment verification
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">
            {isBlocked ? "Account on hold" : needsDocs ? "Additional documents required" : "Awaiting admin verification"}
          </h1>
          <p className="text-white/50 mt-2">
            {isBlocked
              ? sub?.admin_remarks || "Your account is currently blocked. Please contact support@makeyourbrand.in."
              : needsDocs
                ? sub?.admin_remarks || "Our team needs ID + address proof to verify your payment. Upload below."
                : "Our team will review your payment shortly. You'll get an email once approved. This page auto-refreshes."
            }
          </p>
        </header>

        <section className="glass rounded-2xl p-6" data-testid="pending-summary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Plan</div><div className="font-semibold">{sub?.plan_name || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Status</div><div className="font-semibold text-indigo-300">{(sub?.status || "—").replace(/_/g, " ")}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Submitted</div><div className="font-mono text-xs">{sub?.created_at ? new Date(sub.created_at).toLocaleString() : "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Expires</div><div className="font-mono text-xs">{sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}</div></div>
          </div>
          {sub?.admin_remarks && (
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-200">
              <strong>Admin remarks:</strong> {sub.admin_remarks}
            </div>
          )}
        </section>

        {needsDocs && (
          <section className="glass rounded-2xl p-6" data-testid="kyc-upload-section">
            <h3 className="font-heading text-lg font-black mb-4 flex items-center gap-2">
              <Upload className="h-4 w-4 text-indigo-300" /> Upload documents
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="kyc-doc-type"
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="id_proof">ID proof (Aadhaar / PAN / Passport)</option>
                <option value="address_proof">Address proof</option>
                <option value="bank_statement">Bank statement or card statement</option>
                <option value="payment_proof">Payment screenshot</option>
                <option value="other">Other document</option>
              </select>
              <input ref={fileRef} type="file" onChange={upload} data-testid="kyc-upload-file"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                disabled={uploading}
                className="text-sm" />
              {uploading && <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />}
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Submitted documents</div>
              {docs.length === 0 ? <div className="text-sm text-white/40">No documents uploaded yet.</div> : (
                <div className="space-y-2">
                  {docs.map((d) => (
                    <div key={d.id} className="glass rounded-lg p-3 flex items-center gap-3 text-xs" data-testid={`kyc-doc-${d.id}`}>
                      <FileText className="h-3.5 w-3.5 text-white/40" />
                      <span className="text-white/70 flex-1 truncate">{d.filename}</span>
                      <span className="font-mono text-white/40">{d.type}</span>
                      <span className={`px-2 py-0.5 rounded font-mono uppercase text-[10px] ${d.status === "approved" ? "bg-emerald-500/15 text-emerald-300" : d.status === "rejected" ? "bg-rose-500/15 text-rose-300" : "bg-white/5 text-white/60"}`}>{d.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        <section className="glass rounded-2xl p-6" data-testid="pending-invoices">
          <h3 className="font-heading text-lg font-black mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-300" /> Invoices
          </h3>
          {invoices.length === 0 ? <div className="text-sm text-white/40">No invoices yet.</div> : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="glass rounded-lg p-3 flex items-center gap-3 text-xs" data-testid={`invoice-${inv.id}`}>
                  <span className="font-mono text-indigo-300">{inv.invoice_number}</span>
                  <span className="text-white/60 flex-1">{inv.plan_name}</span>
                  <span className="font-mono text-white/70">₹{inv.amount.toLocaleString("en-IN")}</span>
                  <span className={`px-2 py-0.5 rounded font-mono uppercase text-[10px] ${inv.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{inv.payment_status}</span>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                    data-testid={`invoice-pdf-${inv.id}`}
                    className="inline-flex items-center gap-1 text-indigo-300 hover:underline">
                    <Download className="h-3 w-3" /> PDF
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="flex justify-between items-center">
          <button onClick={load} data-testid="pending-refresh" className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-white">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
          <Link to="/dashboard/support" className="text-xs text-indigo-300 hover:underline">Contact support</Link>
        </div>
      </div>
    </div>
  );
}
